import React, { useEffect, useRef, useState } from 'react';
import { loadScriptOnce } from '../../lib/embeds/loadScriptOnce';

const TIKTOK_SCRIPT_SRC = 'https://www.tiktok.com/embed.js';
const INSTAGRAM_SCRIPT_SRC = 'https://www.instagram.com/embed.js';

/**
 * Working Featured Post Embed - Renders actual TikTok/Instagram embeds
 * Falls back to clickable thumbnail if embed fails
 */
function WorkingFeaturedPostEmbed({ post, embedVersion = 0 }) {
  const containerRef = useRef(null);
  const cardRef = useRef(null);
  const [youtubeThumbFailed, setYoutubeThumbFailed] = useState(false);
  const [instagramMode, setInstagramMode] = useState('embed');
  const [tiktokLoaded, setTikTokLoaded] = useState(false);
  const safePost = post || {};
  const { url, platform, thumbnailUrl, title, views, likes } = safePost;
  if (!post || !url) {
    return null;
  }

  useEffect(() => {
    if (!url || !containerRef.current) return;
    let cancelled = false;

    const renderEmbed = async () => {
      try {
        if (platform === 'tiktok') {
          await loadScriptOnce(TIKTOK_SCRIPT_SRC);
          if (!window.tiktok?.embed) {
            await forceReloadTikTokScript();
          }
          if (cancelled || !containerRef.current) return;

          const videoId = extractTikTokId(url);
          containerRef.current.innerHTML = '';
          const blockquote = document.createElement('blockquote');
          blockquote.className = 'tiktok-embed';
          blockquote.setAttribute('cite', url);
          blockquote.setAttribute('data-video-id', videoId || '');
          blockquote.style.maxWidth = '100%';
          blockquote.style.minWidth = '100%';
          blockquote.style.width = '100%';
          blockquote.style.height = '100%';
          blockquote.style.margin = '0';
          blockquote.style.padding = '0';

          const section = document.createElement('section');
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.target = '_blank';
          anchor.rel = 'noopener noreferrer';
          anchor.textContent = 'View on TikTok';
          section.appendChild(anchor);
          blockquote.appendChild(section);
          containerRef.current.appendChild(blockquote);

          const triggerTikTokEmbed = (attempt = 0) => {
            if (cancelled || !containerRef.current) return;
            if (window.tiktok?.embed?.load) {
              window.tiktok.embed.load();
            } else if (window.tiktok?.embed) {
              window.tiktok.embed();
            } else if (attempt < 4) {
              setTimeout(() => triggerTikTokEmbed(attempt + 1), 200);
            }
          };

          requestAnimationFrame(() => {
            triggerTikTokEmbed();
            setTimeout(triggerTikTokEmbed, 400);
            setTimeout(triggerTikTokEmbed, 1000);
          });
        }
      } catch (error) {
        console.warn('Embed failed:', error);
      }
    };

    renderEmbed();

    return () => {
      cancelled = true;
    };
  }, [url, platform, embedVersion]);

  useEffect(() => {
    if (platform === 'youtube') {
      setYoutubeThumbFailed(false);
    }
  }, [platform, url]);

  useEffect(() => {
    if (platform !== 'tiktok') return;
    setTikTokLoaded(false);
    const root = cardRef.current;
    if (!root) return;

    let cancelled = false;
    let observer = null;
    let timeoutId = null;

    const checkLoaded = () => {
      if (cancelled) return false;
      const iframe = root.querySelector('iframe');
      if (iframe) {
        setTikTokLoaded(true);
        return true;
      }
      return false;
    };

    if (!checkLoaded()) {
      observer = new MutationObserver(() => {
        if (checkLoaded()) {
          if (observer) observer.disconnect();
        }
      });
      observer.observe(root, { childList: true, subtree: true });
    }

    timeoutId = setTimeout(() => {
      if (observer) observer.disconnect();
    }, 2500);

    return () => {
      cancelled = true;
      if (observer) observer.disconnect();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [platform, url, embedVersion]);

  useEffect(() => {
    if (platform !== 'instagram') return;
    let cancelled = false;
    const normalizedUrl = normalizeInstagramUrl(url);
    if (!normalizedUrl) {
      setInstagramMode('fallback');
      return;
    }

    setInstagramMode('embed');

    const container = containerRef.current;
    if (!container) return;

    container.innerHTML = '';
    const blockquote = document.createElement('blockquote');
    blockquote.className = 'instagram-media';
    blockquote.setAttribute('data-instgrm-permalink', normalizedUrl);
    blockquote.setAttribute('data-instgrm-version', '14');
    blockquote.style.maxWidth = '100%';
    blockquote.style.minWidth = '100%';
    blockquote.style.width = '100%';
    container.appendChild(blockquote);

    let fallbackTimer = null;
    loadScriptOnce(INSTAGRAM_SCRIPT_SRC)
      .then(() => {
        window.instgrm?.Embeds?.process?.();
        let attempts = 0;
        const check = () => {
          if (cancelled) return;
          attempts += 1;
          if (isInstagramEmbedError(container, blockquote)) {
            setInstagramMode('fallback');
            return;
          }
          if (isInstagramEmbedReady(container, blockquote)) {
            return;
          }
          if (attempts >= 3) {
            setInstagramMode('fallback');
            return;
          }
          fallbackTimer = setTimeout(check, 900);
        };
        fallbackTimer = setTimeout(check, 1200);
      })
      .catch(() => {
        if (!cancelled) {
          setInstagramMode('fallback');
        }
      });

    return () => {
      cancelled = true;
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
      }
    };
  }, [platform, url, embedVersion]);

  const handleClick = () => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  if (platform === 'instagram') {
    if (instagramMode === 'embed') {
      return (
        <div className="relative w-full aspect-[9/16] overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
          <div ref={containerRef} className="absolute inset-0" />
        </div>
      );
    }

    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="group w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm hover:border-slate-300 hover:shadow-md transition"
      >
        <div className="flex items-center justify-between px-3 py-2 bg-slate-100 text-slate-700">
          <div className="flex items-center gap-2 text-sm font-medium">
            <InstagramIcon className="h-4 w-4 text-slate-700" />
            Instagram
          </div>
          <ExternalLinkIcon className="h-4 w-4 text-slate-500 group-hover:text-slate-700" />
        </div>
        <div className="h-[170px] w-full flex items-center justify-center">
          <div className="flex flex-col items-center gap-2 text-slate-700">
            <InstagramIcon className="h-8 w-8" />
            <span className="text-sm font-semibold">View on Instagram</span>
          </div>
        </div>
      </a>
    );
  }

  if (platform === 'tiktok') {
    return (
      <div ref={cardRef} className="w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="relative w-full aspect-[9/16]">
          <div
            className={`absolute inset-0 transition-opacity duration-300 ${
              tiktokLoaded ? 'opacity-0 pointer-events-none' : 'opacity-100'
            }`}
          >
            <div className="h-full w-full animate-pulse bg-slate-200" />
            <div className="absolute inset-x-0 bottom-0 p-3">
              <div className="h-3 w-1/2 rounded bg-slate-300" />
              <div className="mt-2 h-3 w-1/3 rounded bg-slate-300" />
            </div>
          </div>
          <div
            className={`absolute inset-0 transition-opacity duration-300 ${
              tiktokLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div ref={containerRef} className="absolute inset-0" />
            {thumbnailUrl && (
              <div className="absolute inset-0 -z-10">
                <img
                  src={thumbnailUrl}
                  alt={title || 'Featured post'}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (platform === 'youtube') {
    const videoId = extractYouTubeId(url);
    const fallbackLabel = title || 'View on YouTube';
    const thumbnailSrc = videoId
      ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
      : '';
    const viewCount = normalizeCount(views);
    const likeCount = normalizeCount(likes);
    const metaParts = [];
    if (viewCount !== null) {
      metaParts.push(`${formatCount(viewCount)} views`);
    }
    if (likeCount !== null) {
      metaParts.push(`${formatCount(likeCount)} likes`);
    }

    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="group w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm hover:border-slate-300 hover:shadow-md transition"
      >
        <div className={`flex items-center justify-between px-3 py-2 h-[40px] ${getYouTubeStripClass(url)} text-slate-700`}>
          <div className="flex items-center gap-2 text-sm font-medium">
            <YouTubeIcon className="h-4 w-4 text-slate-700" />
            YouTube
          </div>
        </div>
        <div className="relative h-[170px] w-full overflow-hidden bg-slate-100">
          {thumbnailSrc && !youtubeThumbFailed && (
            <img
              src={thumbnailSrc}
              alt={fallbackLabel}
              className="h-full w-full object-cover"
              onError={() => setYoutubeThumbFailed(true)}
            />
          )}
          {!thumbnailSrc || youtubeThumbFailed ? (
            <div className="h-full w-full flex items-center justify-center text-slate-400">
              <YouTubeIcon className="h-10 w-10" />
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-10 w-10 rounded-full bg-white/90 text-slate-900 flex items-center justify-center shadow-sm">
                <PlayIcon className="h-5 w-5 translate-x-[1px]" />
              </div>
            </div>
          )}
        </div>
        <div className="p-3 space-y-1">
          <p className="text-sm font-medium text-slate-900 line-clamp-2">
            {fallbackLabel}
          </p>
          {metaParts.length > 0 && (
            <p className="text-xs text-slate-500">
              {metaParts.join(' • ')}
            </p>
          )}
        </div>
      </a>
    );
  }

  return (
    <div 
      className="relative w-full aspect-[9/16] overflow-hidden rounded-lg border border-slate-200 bg-slate-50 cursor-pointer hover:border-slate-300 transition-colors"
      onClick={handleClick}
    >
      {/* Embed container */}
      <div ref={containerRef} className="absolute inset-0" />
      
      {/* Fallback: show thumbnail while embed loads */}
      {thumbnailUrl && (
        <div className="absolute inset-0 -z-10">
          <img
            src={thumbnailUrl}
            alt={title || 'Featured post'}
            className="h-full w-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}
    </div>
  );
}

function extractTikTokId(url) {
  try {
    const match = url.match(/\/video\/(\d+)/);
    return match ? match[1] : '';
  } catch {
    return '';
  }
}

function forceReloadTikTokScript() {
  return new Promise((resolve, reject) => {
    try {
      const existingScripts = Array.from(document.querySelectorAll('script'))
        .filter((script) => script.src && script.src.includes('tiktok.com/embed.js'));
      existingScripts.forEach((script) => script.remove());
      if (window.tiktok) {
        delete window.tiktok;
      }

      const script = document.createElement('script');
      script.src = `${TIKTOK_SCRIPT_SRC}?v=${Date.now()}`;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to reload TikTok script'));
      document.body.appendChild(script);
    } catch (error) {
      reject(error);
    }
  });
}

function extractYouTubeId(url) {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes('youtu.be')) {
      return parsed.pathname.replace('/', '') || null;
    }
    const v = parsed.searchParams.get('v');
    if (v) return v;
    if (parsed.pathname.includes('/shorts/')) {
      const parts = parsed.pathname.split('/shorts/');
      return parts[1]?.split('/')[0] || null;
    }
  } catch {
    // Fall through to regex-based extraction
  }
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/);
  return match ? match[1] : null;
}

function normalizeCount(value) {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/,/g, '').trim());
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

function formatCount(value) {
  if (typeof value !== 'number') return '';
  return value.toLocaleString();
}

const YOUTUBE_STRIP_CLASSES = [
  'bg-slate-100',
  'bg-blue-50',
  'bg-emerald-50',
  'bg-amber-50',
  'bg-violet-50',
  'bg-rose-50',
];

function getYouTubeStripClass(seed) {
  if (!seed) return YOUTUBE_STRIP_CLASSES[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  const index = Math.abs(hash) % YOUTUBE_STRIP_CLASSES.length;
  return YOUTUBE_STRIP_CLASSES[index];
}

function PlayIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M8 5.5v13l11-6.5-11-6.5z" />
    </svg>
  );
}

function YouTubeIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M23 7.5a4.5 4.5 0 0 0-3.2-3.2C17.8 4 12 4 12 4s-5.8 0-7.8.3A4.5 4.5 0 0 0 1 7.5C.7 9.5.7 12 .7 12s0 2.5.3 4.5a4.5 4.5 0 0 0 3.2 3.2C6.2 20 12 20 12 20s5.8 0 7.8-.3a4.5 4.5 0 0 0 3.2-3.2c.3-2 .3-4.5.3-4.5s0-2.5-.3-4.5zM10 15.5v-7l6 3.5-6 3.5z" />
    </svg>
  );
}

function InstagramIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="4" y="4" width="16" height="16" rx="4" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17" cy="7" r="1.25" fill="currentColor" stroke="none" />
    </svg>
  );
}

function ExternalLinkIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M7 17h10a2 2 0 0 0 2-2V7" />
      <path d="M10 14L19 5" />
      <path d="M14 5h5v5" />
    </svg>
  );
}

function normalizeInstagramUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  try {
    const parsed = new URL(rawUrl);
    if (!parsed.hostname.includes('instagram.com') && !parsed.hostname.includes('instagr.am')) {
      return null;
    }
    const path = parsed.pathname.replace(/\/+$/, '');
    const isCanonical = path.startsWith('/p/') || path.startsWith('/reel/');
    if (!isCanonical) return null;
    return `https://www.instagram.com${path}/`;
  } catch {
    return null;
  }
}

function isInstagramEmbedError(container, blockquote) {
  const target = blockquote || container;
  if (!target) return false;
  const text = (target.textContent || '').toLowerCase();
  if (!text) return false;
  const errorPhrases = [
    'may be broken',
    'may have been removed',
    'content is unavailable',
    'this post is unavailable',
    'visit instagram',
  ];
  return errorPhrases.some((phrase) => text.includes(phrase));
}

function isInstagramEmbedReady(container, blockquote) {
  if (!container) return false;
  if (blockquote?.classList?.contains('instagram-media-rendered')) {
    return true;
  }
  if (container.querySelector('iframe')) {
    return true;
  }
  const height = Math.max(container.offsetHeight, container.scrollHeight);
  if (height > 160) {
    return true;
  }
  return container.childElementCount > 0;
}

export default WorkingFeaturedPostEmbed;
